import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
  addDoc,
  runTransaction
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { Reservation, TimeSlot, Vessel, AvailabilityBlock } from '../../models/types';
import { db } from '../config';
import { getCurrentUserId } from '../../utils/auth';
import { getVesselById, getVesselsByType } from './vesselService';
import { getMarinaSettings } from './marinaSettingsService';
import { parse, isWithinInterval, parseISO, format } from 'date-fns';
import { generateReservationId } from '../../utils/generators';
import { createAvailabilityBlock } from './availabilityService';
import { applyPromoCode } from './discountService';
import { sendEmail } from './emailService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config';
import { formatPhoneNumber } from '../../utils/formatters';
import { EmailTemplate } from './emailService';
import { getCustomerById } from './customerVesselService';

// Collection references
const reservationsRef = collection(db, 'reservations');
const vesselsRef = collection(db, 'vessels');
const availabilityRef = collection(db, 'availability');

// Find an available vessel of the specified type
export const findAvailableVessel = async (
  vesselType: string,
  partySize: number,
  date: string,
  timeSlot: TimeSlot
): Promise<string | null> => {
  try {
    console.log(`Finding available vessel: type=${vesselType}, partySize=${partySize}, date=${date}, timeSlot=${timeSlot}`);
    
    // Get all vessels of the specified type
    const vesselsRef = collection(db, 'vessels');
    const q = query(
      vesselsRef,
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const vessels = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Vessel));
    
    // Debug logging for all vessels found before filtering
    console.log('All vessels before filtering:', vessels.map(v => ({
      id: v.id,
      name: v.name,
      type: v.type,
      capacity: v.capacity,
      isActive: v.isActive,
      status: v.status
    })));
    
    // Filter manually for type (case-insensitive) and capacity
    const filteredVessels = vessels.filter(vessel => {
      // Case-insensitive type comparison
      const typeMatches = vessel.type && typeof vessel.type === 'string' && 
                         vessel.type.toLowerCase() === vesselType.toLowerCase();
      
      // Convert capacity to number if it's a string
      const vesselCapacity = typeof vessel.capacity === 'string' ? parseInt(vessel.capacity) : vessel.capacity;
      const capacityMatches = vesselCapacity >= partySize;
      
      return typeMatches && capacityMatches;
    });
    
    console.log(`Found ${filteredVessels.length} vessels matching criteria:`, filteredVessels);
    
    // Check availability for each vessel
    for (const vessel of filteredVessels) {
      console.log(`Checking availability for vessel: ${vessel.id} (${vessel.name})`);
      try {
        const isAvailable = await checkAvailability(vessel.id, date, timeSlot);
        console.log(`Vessel ${vessel.id} availability: ${isAvailable}`);
        
        if (isAvailable) {
          return vessel.id;
        }
      } catch (error: any) {
        // If the error is about advance booking window, rethrow it
        if (error.message && error.message.includes('advance booking window')) {
          throw error;
        }
        // Otherwise, log the error and continue checking other vessels
        console.error(`Error checking availability for vessel ${vessel.id}:`, error);
      }
    }
    
    console.log('No available vessels found');
    return null;
  } catch (error) {
    console.error('Error finding available vessel:', error);
    throw error;
  }
};

// Create a new reservation with atomic availability check
export const createReservation = async (
  reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Reservation> => {
  try {
    console.log('Creating reservation with atomic transaction:', {
      ...reservation,
      locationId: reservation.locationId
    });
    
    // Use Firestore transaction for atomic booking
    const reservationDoc = await runTransaction(db, async (transaction) => {
      const id = crypto.randomUUID();
      const now = new Date();
      
      // Step 1: Find and verify available vessel within transaction
      const availableVesselId = await findAvailableVesselInTransaction(
        transaction,
        reservation.vesselType,
        reservation.partySize,
        reservation.date,
        reservation.timeSlot
      );
      
      if (!availableVesselId) {
        throw new Error('No vessels available for the selected time slot');
      }
      
      // Step 2: Double-check that the specific vessel is still available within the transaction
      const conflictQuery = query(
        collection(db, 'reservations'),
        where('vesselId', '==', availableVesselId),
        where('date', '==', reservation.date),
        where('status', 'in', ['pending', 'confirmed'])
      );
      
      const conflictSnapshot = await getDocs(conflictQuery);
      
      // Check for time slot conflicts
      for (const doc of conflictSnapshot.docs) {
        const existingReservation = doc.data() as Reservation;
        if (timeSlotOverlaps(reservation.timeSlot, existingReservation.timeSlot)) {
          throw new Error('Time slot conflict detected during booking process');
        }
      }
      
      // Step 3: Create the reservation document atomically
      const reservationDoc: Reservation = {
        ...reservation,
        id,
        vesselId: availableVesselId,
        totalPrice: reservation.totalPrice || reservation.finalPrice, // Ensure totalPrice is set
        version: 1, // Initialize version for optimistic locking
        createdAt: now,
        updatedAt: now
      };
      
      const reservationRef = doc(db, 'reservations', id);
      transaction.set(reservationRef, reservationDoc);
      
      // Step 4: Optionally create an availability block for this time slot
      const availabilityBlockRef = doc(db, 'availability', `${availableVesselId}_${reservation.date}_${reservation.timeSlot}`);
      transaction.set(availabilityBlockRef, {
        id: `${availableVesselId}_${reservation.date}_${reservation.timeSlot}`,
        vesselId: availableVesselId,
        date: reservation.date,
        timeSlot: reservation.timeSlot,
        isAvailable: false,
        reservationId: id,
        createdAt: now.getTime(),
        updatedAt: now.getTime()
      });
      
      console.log(`Reservation ${id} created atomically for vessel ${availableVesselId}`);
      return reservationDoc;
    });
    
    // Send confirmation email AFTER transaction completes successfully
    try {
      await sendReservationConfirmationEmail(reservationDoc);
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      // Don't fail the reservation creation if email fails
    }
    
    return reservationDoc;
     
  } catch (error) {
    console.error('Error creating reservation:', error);
    if (error instanceof Error) {
      // Re-throw with more specific error messages
      if (error.message.includes('No vessels available') || error.message.includes('Time slot conflict')) {
        throw error;
      }
    }
    throw new Error('Unable to complete reservation due to availability conflict');
  }
};

// Separate function for sending confirmation emails
const sendReservationConfirmationEmail = async (reservation: Reservation): Promise<void> => {
  try {
    console.log('Sending confirmation email for reservation:', reservation.id);
    
    // Get the default reservation confirmation template from the location's templates
    const templatesRef = collection(db, `locations/${reservation.locationId}/transactionalEmailTemplates`);
    const q = query(templatesRef, 
      where('type', '==', 'reservation_confirmation'),
      where('isDefault', '==', true)
    );
    const querySnapshot = await getDocs(q);
    
    let template: EmailTemplate;
    
    if (querySnapshot.empty) {
      console.warn('No default reservation confirmation template found, looking for any template');
      // Try to get any reservation confirmation template, not just default
      const q2 = query(templatesRef, where('type', '==', 'reservation_confirmation'));
      const querySnapshot2 = await getDocs(q2);
      
      if (querySnapshot2.empty) {
        console.error('No reservation confirmation templates found at all');
        return;
      }
      
      template = querySnapshot2.docs[0].data() as EmailTemplate;
    } else {
      template = querySnapshot.docs[0].data() as EmailTemplate;
    }
    
    // Get marina information
    const marinaDoc = await getDoc(doc(db, 'locations', reservation.locationId));
    const marinaData = marinaDoc.data();
    
    if (!marinaData) {
      console.error('Marina data not found for location:', reservation.locationId);
      return;
    }
    
    // Format date properly for display
    const localDate = parse(reservation.date, 'yyyy-MM-dd', new Date());
    const formattedDate = localDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Call Firebase Function to send email
    const sendEmailFunction = httpsCallable(functions, 'sendEmail');
    await sendEmailFunction({
      to: reservation.customerDetails.email,
      subject: template.subject,
      body: template.body,
      templateId: template.id,
      reservationId: reservation.id,
      // Reservation data
      id: reservation.id,
      customerDetails_firstName: reservation.customerDetails.firstName,
      customerDetails_lastName: reservation.customerDetails.lastName,
      customerDetails_email: reservation.customerDetails.email,
      customerDetails_phone: formatPhoneNumber(reservation.customerDetails.phone),
      vesselName: reservation.vesselName,
      vesselType: reservation.vesselType,
      date: formattedDate,
      partySize: reservation.partySize,
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      originalPrice: reservation.originalPrice?.toFixed(2),
      damageWaiverAmount: reservation.damageWaiverAmount?.toFixed(2),
      taxAmount: reservation.taxAmount?.toFixed(2),
      finalPrice: reservation.finalPrice?.toFixed(2),
      status: reservation.status,
      // Payment data
      payment_depositAmount: reservation.payment?.depositAmount?.toFixed(2),
      payment_remainingAmount: reservation.payment?.remainingAmount?.toFixed(2),
      // Marina data
      marinaName: marinaData.name,
      marinaPhone: formatPhoneNumber(marinaData.phone),
      marinaEmail: marinaData.email,
      marinaAddress: marinaData.address,
      marinaWebsite: marinaData.website
    });
    
    console.log('Confirmation email sent successfully');
  } catch (error) {
    console.error('Error in sendReservationConfirmationEmail:', error);
    throw error;
  }
};

// Helper function to find available vessel within a transaction context
const findAvailableVesselInTransaction = async (
  transaction: any,
  vesselType: string,
  partySize: number,
  date: string,
  timeSlot: TimeSlot
): Promise<string | null> => {
  console.log(`Finding available vessel in transaction: type=${vesselType}, date=${date}, timeSlot=${timeSlot}`);
  
  // Get all vessels of the specified type
  const vesselsQuery = query(
    collection(db, 'vessels'),
    where('type', '==', vesselType),
    where('isActive', '==', true)
  );
  
  const vesselsSnapshot = await getDocs(vesselsQuery);
  const vessels = vesselsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Vessel));
  
  console.log(`Found ${vessels.length} vessels of type ${vesselType}`);
  
  // Filter by capacity
  const suitableVessels = vessels.filter(vessel => {
    const vesselCapacity = typeof vessel.capacity === 'string' ? parseInt(vessel.capacity) : vessel.capacity;
    return vesselCapacity >= partySize;
  });
  
  console.log(`${suitableVessels.length} vessels meet capacity requirements`);
  
  // Check each vessel for availability
  for (const vessel of suitableVessels) {
    const reservationsQuery = query(
      collection(db, 'reservations'),
      where('vesselId', '==', vessel.id),
      where('date', '==', date),
      where('status', 'in', ['pending', 'confirmed'])
    );
    
    const reservationsSnapshot = await getDocs(reservationsQuery);
    
    // Check if this vessel has any conflicting reservations
    let hasConflict = false;
    for (const doc of reservationsSnapshot.docs) {
      const reservation = doc.data() as Reservation;
      if (timeSlotOverlaps(timeSlot, reservation.timeSlot)) {
        hasConflict = true;
        break;
      }
    }
    
    if (!hasConflict) {
      console.log(`Vessel ${vessel.id} (${vessel.name}) is available`);
      return vessel.id;
    }
  }
  
  console.log('No available vessels found');
  return null;
};

// Helper function to check if time slots overlap
const timeSlotOverlaps = (slot1: TimeSlot, slot2: TimeSlot): boolean => {
  const convertTimeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  if (!slot1.includes('-') || !slot2.includes('-')) {
    return false;
  }
  
  const [start1, end1] = slot1.split('-');
  const [start2, end2] = slot2.split('-');
  
  const start1Minutes = convertTimeToMinutes(start1);
  const end1Minutes = convertTimeToMinutes(end1);
  const start2Minutes = convertTimeToMinutes(start2);
  const end2Minutes = convertTimeToMinutes(end2);
  
  // Two time slots overlap if:
  // - The start of one is less than the end of the other
  // - The end of one is greater than the start of the other
  return start1Minutes < end2Minutes && end1Minutes > start2Minutes;
};

// Get all reservations
export const getAllReservations = async (): Promise<Reservation[]> => {
  try {
    const querySnapshot = await getDocs(reservationsRef);
    return querySnapshot.docs.map(doc => {
      const data = doc.data() as Reservation;
      // Include document ID directly in the reservation data
      return { ...data, id: doc.id };
    });
  } catch (error) {
    console.error('Error getting reservations:', error);
    throw error;
  }
};

// Get reservation by ID with hybrid customer details support
export const getReservationById = async (reservationId: string): Promise<Reservation | null> => {
  try {
    const reservationDoc = await getDoc(doc(reservationsRef, reservationId));
    
    if (reservationDoc.exists()) {
      const data = reservationDoc.data() as any;
      // Convert all timestamp fields to ISO strings
      const toISOString = (value: any) => {
        if (!value) return undefined;
        if (value instanceof Date) return value.toISOString();
        if (typeof value.toDate === 'function') return value.toDate().toISOString(); // Firestore Timestamp
        if (typeof value === 'string') return value; // already string
        return new Date(value).toISOString();
      };
      
      const reservation: any = {
        ...data,
        id: reservationDoc.id,
        createdAt: toISOString(data.createdAt),
        updatedAt: toISOString(data.updatedAt),
        cancelledAt: toISOString(data.cancelledAt),
        checkInTime: toISOString(data.checkInTime),
        checkOutTime: toISOString(data.checkOutTime)
      };
      
      // Ensure customer details are populated using hybrid approach
      const customerDetails = await getReservationCustomerDetails(reservation);
      if (customerDetails) {
        reservation.customerDetails = customerDetails;
      } else {
        // Fallback to prevent crashes
        reservation.customerDetails = {
          firstName: 'N/A',
          lastName: '',
          email: 'N/A',
          phone: ''
        };
      }
      
      console.log('Converted reservation data with customer details:', reservation);
      return reservation as Reservation;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting reservation:', error);
    throw error;
  }
};

// Update reservation with optimistic locking
export const updateReservation = async (reservationId: string, reservationData: Partial<Reservation>): Promise<void> => {
  try {
    // Use transaction for optimistic locking
    await runTransaction(db, async (transaction) => {
      const reservationRef = doc(reservationsRef, reservationId);
      const reservationDoc = await transaction.get(reservationRef);
      
      if (!reservationDoc.exists()) {
        throw new Error('Reservation not found');
      }
      
      const currentReservation = reservationDoc.data() as Reservation;
      const expectedVersion = reservationData.version;
      
      // Check version for optimistic locking
      if (expectedVersion && currentReservation.version !== expectedVersion) {
        throw new Error('Reservation has been modified by another user. Please refresh and try again.');
      }
      
      // Increment version and update
      const updatedData = {
        ...reservationData,
        version: (currentReservation.version || 0) + 1,
        updatedAt: Date.now()
      };
      
      transaction.update(reservationRef, updatedData);
    });
  } catch (error) {
    console.error('Error updating reservation:', error);
    throw error;
  }
};

// Delete reservation
export const deleteReservation = async (reservationId: string): Promise<void> => {
  try {
    await deleteDoc(doc(reservationsRef, reservationId));
  } catch (error) {
    console.error('Error deleting reservation:', error);
    throw error;
  }
};

// Get reservations by customer
export const getReservationsByCustomer = async (customerId: string): Promise<Reservation[]> => {
  try {
    const q = query(
      reservationsRef,
      where('customerId', '==', customerId),
      orderBy('date', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as Reservation);
  } catch (error) {
    console.error('Error getting customer reservations:', error);
    throw error;
  }
};

// Get reservations by vessel
export const getReservationsByVessel = async (vesselId: string): Promise<Reservation[]> => {
  try {
    const q = query(
      reservationsRef,
      where('vesselId', '==', vesselId),
      orderBy('date', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const reservations = querySnapshot.docs.map(doc => doc.data() as Reservation);
    
    // Log the reservations for debugging
    console.log('Retrieved reservations:', reservations);
    
    // Sort the results in memory and filter for future reservations
    return reservations
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .filter(reservation => {
        const reservationDate = new Date(reservation.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return reservationDate >= today;
      });
  } catch (error) {
    console.error('Error getting vessel reservations:', error);
    throw error;
  }
};

// Get reservations by date range
export const getReservationsByDateRange = async (startDate: string, endDate: string): Promise<Reservation[]> => {
  try {
    const q = query(
      reservationsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as Reservation);
  } catch (error) {
    console.error('Error getting reservations by date range:', error);
    throw error;
  }
};

// Check availability
export const checkAvailability = async (
  vesselId: string,
  date: string,
  timeSlot: TimeSlot
): Promise<boolean> => {
  try {
    console.log(`Checking availability for vessel ${vesselId} on ${date} for time slot ${timeSlot}`);
    
    // Validate inputs
    if (!vesselId || !date || !timeSlot) {
      console.error('Missing required parameters for availability check');
      return false;
    }
    
    // Parse the requested time slot to get start and end times in minutes
    let requestedSlotStart = 0;
    let requestedSlotEnd = 0;
    
    // Function to convert time string "HH:MM" to minutes since midnight
    const convertTimeToMinutes = (timeStr: string): number => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    // Check if this is a custom time slot in the format "HH:MM-HH:MM"
    if (timeSlot.includes('-')) {
      // Parse the start and end times from the time slot string
      const [start, end] = timeSlot.split('-');
      requestedSlotStart = convertTimeToMinutes(start);
      requestedSlotEnd = convertTimeToMinutes(end);
    } else {
      console.error(`Invalid time slot format: ${timeSlot}. Expected format: "HH:MM-HH:MM"`);
      return false;
    }
    
    // Get all reservations for this vessel and date, regardless of time slot
    const q = query(
      reservationsRef,
      where('vesselId', '==', vesselId),
      where('date', '==', date),
      where('status', 'in', ['pending', 'confirmed'])
    );
    
    const querySnapshot = await getDocs(q);
    
    // Check for overlapping reservations
    for (const doc of querySnapshot.docs) {
      const reservation = doc.data() as Reservation;
      
      // Parse the existing reservation's time slot
      if (reservation.timeSlot && reservation.timeSlot.includes('-')) {
        const [existingStart, existingEnd] = reservation.timeSlot.split('-');
        const existingSlotStart = convertTimeToMinutes(existingStart);
        const existingSlotEnd = convertTimeToMinutes(existingEnd);
        
        // Check if there's any overlap between the requested time slot and the existing one
        // Two time slots overlap if:
        // - The start of one is less than the end of the other
        // - The end of one is greater than the start of the other
        if (requestedSlotStart < existingSlotEnd && requestedSlotEnd > existingSlotStart) {
          console.log(`Vessel ${vesselId} already has an overlapping reservation for ${date}`);
          console.log(`Requested: ${timeSlot}, Existing: ${reservation.timeSlot}`);
          return false;
        }
      }
    }
    
    // 2. Get marina settings to check business hours and rules
    const marinaSettings = await getMarinaSettings();
    console.log('Marina settings:', marinaSettings);
    
    // 3. Check if the date is within the advance booking window
    const { advanceBookingDays } = marinaSettings.reservationRules;
    const currentDate = new Date();
    const reservationDate = parseISO(date);
    const advanceLimit = new Date();
    advanceLimit.setDate(currentDate.getDate() + advanceBookingDays);
    
    if (reservationDate > advanceLimit) {
      // Date is too far in the future according to reservation rules
      console.log(`Date ${date} is beyond the ${advanceBookingDays} day advance booking window`);
      throw new Error(`Date ${date} is beyond the ${advanceBookingDays} day advance booking window`);
    }
    
    // 4. Check business hours - determine if marina is open on that day
    const dayOfWeek = format(reservationDate, 'EEEE').toLowerCase();
    
    // Check if the date is in season
    let isInSeason = false;
    if (marinaSettings.isSeasonalHoursEnabled && marinaSettings.seasonStartDate && marinaSettings.seasonEndDate) {
      // Parse the season start date
      const seasonStartDate = marinaSettings.seasonStartDate;
      let seasonStart;
      
      // Handle both date string formats: "MM-DD" and "YYYY-MM-DD"
      if (seasonStartDate.includes('-')) {
        if (seasonStartDate.split('-').length === 3) {
          // Full date with year
          seasonStart = parseISO(seasonStartDate);
        } else {
          // MM-DD format, add current year
          const currentYear = reservationDate.getFullYear();
          const [month, day] = seasonStartDate.split('-').map(Number);
          seasonStart = new Date(currentYear, month - 1, day);
        }
      }
      
      // Parse the season end date
      const seasonEndDate = marinaSettings.seasonEndDate;
      let seasonEnd;
      
      // Handle both date string formats: "MM-DD" and "YYYY-MM-DD"
      if (seasonEndDate.includes('-')) {
        if (seasonEndDate.split('-').length === 3) {
          // Full date with year
          seasonEnd = parseISO(seasonEndDate);
        } else {
          // MM-DD format, add current year
          const currentYear = reservationDate.getFullYear();
          const [month, day] = seasonEndDate.split('-').map(Number);
          seasonEnd = new Date(currentYear, month - 1, day);
        }
      }
      
      // Check if reservationDate is within season
      if (seasonStart && seasonEnd) {
        isInSeason = isWithinInterval(reservationDate, { start: seasonStart, end: seasonEnd });
        console.log(`Seasonal check for ${format(reservationDate, 'EEE MMM dd yyyy')}: season=${format(seasonStart, 'EEE MMM dd yyyy')} to ${format(seasonEnd, 'EEE MMM dd yyyy')}, isInSeason=${isInSeason}, checkDay >= seasonStart: ${reservationDate >= seasonStart}, checkDay <= seasonEnd: ${reservationDate <= seasonEnd}`);
      }
    }
    
    // Use the appropriate business hours based on season
    let businessHours;
    if (marinaSettings.isSeasonalHoursEnabled) {
      businessHours = isInSeason
        ? (marinaSettings.inSeasonHours || marinaSettings.businessHours)
        : (marinaSettings.outOfSeasonHours || marinaSettings.businessHours);
    } else {
      businessHours = marinaSettings.businessHours;
    }
    
    const dayHours = businessHours[dayOfWeek];
    
    if (!dayHours || dayHours.isClosed) {
      console.log(`Marina is closed on ${dayOfWeek}`);
      return false;
    }
    
    // 5. Check if the time slot is within business hours
    const openTimeMinutes = convertTimeToMinutes(dayHours.openTime);
    const closeTimeMinutes = convertTimeToMinutes(dayHours.closeTime);
    
    const isSlotWithinBusinessHours = 
      requestedSlotStart >= openTimeMinutes && 
      requestedSlotEnd <= closeTimeMinutes;
    
    if (!isSlotWithinBusinessHours) {
      console.log(`Requested time slot ${timeSlot} is outside business hours for ${dayOfWeek}`);
      return false;
    }
    
    // If we've passed all checks, the vessel is available for this date and time slot
    console.log(`Vessel ${vesselId} is available for ${date} at ${timeSlot}`);
    return true;
  } catch (error) {
    console.error('Error checking availability:', error);
    throw error;
  }
};

// Update vessel availability for a specific date and time slot
export const updateVesselAvailability = async (
  vesselId: string,
  date: string,
  timeSlot: TimeSlot,
  isAvailable: boolean
): Promise<void> => {
  try {
    await createAvailabilityBlock(vesselId, date, timeSlot, isAvailable);
  } catch (error) {
    console.error('Error updating vessel availability:', error);
    throw error;
  }
};

// Add comprehensive double booking monitoring
export const detectDoubleBookings = async (): Promise<any[]> => {
  try {
    console.log('Starting double booking detection scan...');
    
    const reservationsQuery = query(
      collection(db, 'reservations'),
      where('status', 'in', ['pending', 'confirmed']),
      orderBy('date', 'asc')
    );
    
    const snapshot = await getDocs(reservationsQuery);
    const reservations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Reservation));
    
    const conflicts: any[] = [];
    const seenSlots = new Map<string, any[]>();
    
    reservations.forEach(reservation => {
      const key = `${reservation.vesselId}_${reservation.date}_${reservation.timeSlot}`;
      
      if (!seenSlots.has(key)) {
        seenSlots.set(key, []);
      }
      
      seenSlots.get(key)!.push(reservation);
    });
    
    // Find conflicts
    seenSlots.forEach((reservationList, key) => {
      if (reservationList.length > 1) {
        conflicts.push({
          conflictKey: key,
          reservations: reservationList,
          conflictCount: reservationList.length
        });
      }
    });
    
    if (conflicts.length > 0) {
      console.error('DOUBLE BOOKING DETECTED:', conflicts);
      
      // Send alert (implement your alerting mechanism here)
      await sendDoubleBookingAlert(conflicts);
    }
    
    return conflicts;
  } catch (error) {
    console.error('Error detecting double bookings:', error);
    throw error;
  }
};

// Send alert for double bookings
const sendDoubleBookingAlert = async (conflicts: any[]): Promise<void> => {
  try {
    // Log critical error
    console.error('🚨 CRITICAL: Double booking detected!', {
      timestamp: new Date().toISOString(),
      conflictCount: conflicts.length,
      conflicts: conflicts.map(c => ({
        key: c.conflictKey,
        reservationIds: c.reservations.map((r: any) => r.id)
      }))
    });
    
    // You can implement additional alerting here:
    // - Send email to administrators
    // - Log to external monitoring service
    // - Create support tickets
    // - Send Slack/Discord notifications
    
  } catch (error) {
    console.error('Error sending double booking alert:', error);
  }
};

// Validate reservation before creation (additional safety check)
export const validateReservationIntegrity = async (reservation: any): Promise<boolean> => {
  try {
    // Check 1: Verify vessel exists and is active
    const vesselDoc = await getDoc(doc(db, 'vessels', reservation.vesselId));
    if (!vesselDoc.exists() || !vesselDoc.data()?.isActive) {
      throw new Error('Invalid vessel or vessel is inactive');
    }
    
    // Check 2: Verify date is not in the past
    const reservationDate = new Date(reservation.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (reservationDate < today) {
      throw new Error('Cannot book dates in the past');
    }
    
    // Check 3: Verify time slot format
    if (!reservation.timeSlot || !reservation.timeSlot.includes('-')) {
      throw new Error('Invalid time slot format');
    }
    
    // Check 4: Verify vessel capacity
    const vessel = vesselDoc.data();
    const vesselCapacity = typeof vessel.capacity === 'string' ? parseInt(vessel.capacity) : vessel.capacity;
    if (reservation.partySize > vesselCapacity) {
      throw new Error('Party size exceeds vessel capacity');
    }
    
    return true;
  } catch (error) {
    console.error('Reservation validation failed:', error);
    throw error;
  }
};

/**
 * Hybrid function to get customer details from either embedded customerDetails (old format)
 * or by fetching from customers collection using customerId (new format)
 * This ensures backwards and forwards compatibility
 */
export const getReservationCustomerDetails = async (reservation: any): Promise<{
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
} | null> => {
  try {
    // If reservation has embedded customerDetails (old format), use that
    if (reservation.customerDetails && reservation.customerDetails.firstName) {
      return {
        firstName: reservation.customerDetails.firstName,
        lastName: reservation.customerDetails.lastName,
        email: reservation.customerDetails.email,
        phone: reservation.customerDetails.phone
      };
    }
    
    // If reservation has customerId (new format), fetch customer details
    if (reservation.customerId) {
      const customer = await getCustomerById(reservation.customerId);
      if (customer) {
        return {
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phone: customer.phone || ''
        };
      }
    }
    
    // No customer details found
    return null;
  } catch (error) {
    console.error('Error getting customer details for reservation:', reservation.id, error);
    return null;
  }
};

/**
 * Enhanced getAllReservations that includes customer details for all reservations
 * regardless of whether they use the old or new format
 */
export const getAllReservationsWithCustomers = async (): Promise<Reservation[]> => {
  try {
    const querySnapshot = await getDocs(reservationsRef);
    
    // Process all reservations and ensure they have customer details
    const reservationsWithCustomers = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const data = doc.data() as any;
        const reservation = { ...data, id: doc.id };
        
        // Get customer details using hybrid approach
        const customerDetails = await getReservationCustomerDetails(reservation);
        
        // If we found customer details, ensure they're in the reservation
        if (customerDetails) {
          reservation.customerDetails = customerDetails;
        } else {
          // Fallback to prevent crashes
          reservation.customerDetails = {
            firstName: 'N/A',
            lastName: '',
            email: 'N/A',
            phone: ''
          };
        }
        
        return reservation as Reservation;
      })
    );
    
    return reservationsWithCustomers;
  } catch (error) {
    console.error('Error getting reservations with customers:', error);
    throw error;
  }
}; 